const DEFAULT_PLAN = 'pro-monthly'

/**
 * Calculate the required plan based on the current plan
 * Plan hierarchy: essential < plus < pro
 * @param {string} requiredPlanLevel - The required plan level (e.g., 'pro', 'essential', 'plus')
 * @param {string} currentPlan - The current plan type (e.g., 'free', 'pro-monthly', 'edu-small')
 * @returns {string} The required plan in format '{requiredPlanLevel}-{planCycle}'
 */
export function getRequiredPlan(requiredPlanLevel, currentPlan) {
  if (!requiredPlanLevel || !currentPlan) {
    return DEFAULT_PLAN
  }

  if (currentPlan.includes('pro') && currentPlan.includes('yearly')) {
    return currentPlan
  }

  if (currentPlan === 'free') {
    return `${requiredPlanLevel}-monthly`
  }

  if (currentPlan.includes('edu')) {
    return `${requiredPlanLevel}-yearly`
  }

  if (requiredPlanLevel === 'essential' && currentPlan.includes('plus') && !currentPlan.includes('trial')) {
    return currentPlan
  }

  if (currentPlan.includes('one-time')) {
    return `${requiredPlanLevel}-monthly`
  }

  if (currentPlan.includes('monthly')) {
    return `${requiredPlanLevel}-monthly`
  }

  if (currentPlan.includes('yearly')) {
    return `${requiredPlanLevel}-yearly`
  }

  return DEFAULT_PLAN
}

// BOO-58 / AHA-43262: variant names for the experiment-direct-feature-upgrade A/B test.
export const DIRECT_FEATURE_UPGRADE_VARIANTS = Object.freeze({
  MONTHLY: 'monthly-upgrade',
  YEARLY: 'yearly-upgrade',
})

/**
 * BOO-58 / AHA-43262 — apply the direct-feature-upgrade billing-cycle experiment.
 *
 * Free users are normally hard-coded to a monthly checkout (getRequiredPlan
 * returns `${level}-monthly`). When the `yearly-upgrade` arm is active we swap a
 * trailing `-monthly` for `-yearly` so the free user is directed to Stripe with a
 * yearly subscription instead. Any other variant (including `monthly-upgrade`,
 * `control`, `disabled`, or a flag that is off) leaves the plan key untouched, so
 * the control arm is identical to today's behaviour.
 *
 * Only the canonical `<level>-monthly` shape is rewritten — legacy dated keys
 * (e.g. `pro-monthly-24122019`) and non-monthly keys are returned unchanged to
 * avoid producing a lookupKey that resolvePlanKey cannot resolve. The match is
 * case-insensitive and tolerates multi-segment levels because the level often
 * comes straight from the server `planBenefit.*_planName` fields, whose casing
 * and shape are not guaranteed (e.g. `Essential`); the rewritten key is always
 * normalised to lowercase so it resolves against the canonical Stripe lookupKey.
 *
 * @param {string} planKey - The plan key from getRequiredPlan (e.g. 'pro-monthly')
 * @param {string} variantName - The Unleash variant name for the experiment
 * @returns {string} The plan key to send to Stripe checkout
 */
export function applyDirectFeatureUpgradeVariant(planKey, variantName) {
  if (!planKey || variantName !== DIRECT_FEATURE_UPGRADE_VARIANTS.YEARLY) {
    return planKey
  }

  // Only swap the canonical, non-dated monthly shape: `<level>-monthly`
  // (one or more lowercase segments, no trailing numeric date suffix).
  // Lowercase first so capitalised server plan names (e.g. `Essential-monthly`)
  // are matched and produce a resolvable lowercase yearly key.
  const normalised = planKey.toLowerCase()
  if (/^[a-z]+(?:-[a-z]+)*-monthly$/.test(normalised)) {
    return normalised.replace(/-monthly$/, '-yearly')
  }

  return planKey
}

// AHA-42054 — when the essential plan is removed for free users, every upgrade
// hint that points at "essential" must point at "pro" instead, so the presenter
// never advertises a tier the pricing table no longer shows. These helpers are
// pure and idempotent; the flag + free-user decision is made by the caller.
const PLAN_NAME_SUFFIX = '_planName'

/**
 * Map a single required-plan level "essential" → "pro" when the override is on.
 * Case-insensitive on input (server `*_planName` casing is not guaranteed); any
 * other level — and any value when `enabled` is false — is returned untouched.
 *
 * @param {string} planLevel - e.g. 'essential', 'pro', 'plus'
 * @param {boolean} enabled - whether the no-essential override applies
 * @returns {string} the (possibly remapped) plan level
 */
export function mapEssentialToPro(planLevel, enabled) {
  if (!enabled || typeof planLevel !== 'string') return planLevel
  return planLevel.toLowerCase() === 'essential' ? 'pro' : planLevel
}

/**
 * Deep-rewrite every `<feature>_planName: "essential"` upgrade hint in a
 * plan-benefit / entitlements response to "pro". Returns a new object (the input
 * is not mutated) so the cached response stays consistent; a no-op when disabled
 * or given a non-object. Only `*_planName` string leaves are touched — boolean
 * feature gates and the plan `name` are left exactly as the server sent them.
 *
 * @param {object} planBenefit - the plan-benefit response body
 * @param {boolean} enabled - whether the no-essential override applies
 * @returns {object} a remapped copy, or the original value when disabled
 */
export function mapEssentialPlanNamesToPro(planBenefit, enabled) {
  if (!enabled || !planBenefit || typeof planBenefit !== 'object') return planBenefit

  const walk = (node) => {
    if (Array.isArray(node)) return node.map(walk)
    if (node && typeof node === 'object') {
      // Build the copy with Object.keys + a plain loop instead of
      // Object.entries(...).map(...) + Object.fromEntries — keeps allocations
      // low on every level of the (recursive) walk, while avoiding for..in
      // (banned by no-restricted-syntax for walking the prototype chain).
      const copy = {}
      Object.keys(node).forEach((key) => {
        const value = node[key]
        if (key.endsWith(PLAN_NAME_SUFFIX) && typeof value === 'string') {
          copy[key] = mapEssentialToPro(value, true)
        } else {
          copy[key] = walk(value)
        }
      })
      return copy
    }
    return node
  }

  return walk(planBenefit)
}
